import React from 'react'
import { DocumentNode } from 'graphql'
import { PureQueryOptions } from 'apollo-client'
import { Query } from 'react-apollo'
import { Aggregations, AggregationOptions } from '.'
import { CollectionVariables } from '../organization-collection-loader'

interface ChildProps<TCategory extends string> {
  readonly aggregations: Aggregations<TCategory>
  readonly queryInfos: PureQueryOptions[]
  readonly loading: boolean
}

interface Props<TCategory extends string> {
  readonly queries: DocumentNode[]
  readonly queryRootKey: string
  readonly organizationID: string
  readonly filterText?: string
  readonly quickFilter?: string
  readonly licensedOnly?: boolean
  readonly aggregationFilters?: AggregationOptions<TCategory>
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly areVariablesEqual?: (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => boolean
  readonly children: (props: ChildProps<TCategory>) => React.ReactElement<any>
}

export type AggregationsLoaderFn<TCategory extends string> = (
  props: Props<TCategory>
) => JSX.Element

export function AggregationsLoader<TCategory extends string>({
  queries,
  queryRootKey,
  organizationID,
  filterText,
  quickFilter,
  licensedOnly,
  aggregationFilters,
  additionalFilters,
  children
}: Props<TCategory>) {
  const variables = Object.assign({}, aggregationFilters, {
    organizationID,
    filterText,
    quickFilter,
    licensedOnly,
    ...additionalFilters
  })

  return (
    <ComposedQuery
      loading={true}
      aggregations={{}}
      queries={queries}
      queryInfos={[]}
      queryRootKey={queryRootKey}
      variables={variables}
      children={children}
    />
  )
}

interface QueryProps<TCategory extends string> {
  readonly loading: boolean
  readonly aggregations: Partial<Aggregations<TCategory>>
  readonly queryInfos: PureQueryOptions[]
  readonly queries: DocumentNode[]
  readonly queryRootKey: string
  readonly variables: any
  readonly children: (props: ChildProps<TCategory>) => React.ReactElement<any>
}

function ComposedQuery<TCategory extends string>({
  loading,
  aggregations,
  queryInfos,
  queries,
  queryRootKey,
  variables,
  children
}: QueryProps<TCategory>) {
  if (queries.length < 1) {
    const allAggregations = aggregations as Aggregations<TCategory>
    return children({ aggregations: allAggregations, queryInfos, loading })
  }

  const [query, ...remainingQueries] = queries

  return (
    <Query query={query} variables={variables}>
      {({ data, loading }) => {
        const newAggregations = (data && data[queryRootKey]) || []

        return (
          <ComposedQuery
            loading={loading}
            aggregations={{ ...aggregations, ...newAggregations }}
            queries={remainingQueries}
            queryInfos={[...queryInfos, { query, variables }]}
            queryRootKey={queryRootKey}
            variables={variables}
            children={children}
          />
        )
      }}
    </Query>
  )
}
